import React, { Context, createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { TRequestWithCustom } from '../../utils/custom-fields'
import { callApi, TApiResponse } from '../api'
import { IAccount, IUser, TAccountRequest, TEeoRequest, TAccountActions, TChannel } from '../types'
import { getCallEmailText, profileHeaderToken } from '../utils'

type UrlParam = {
	accountId: string
}

const initialAccountState: IAccount = {
	user: null,
	token: null,
	channels: [],
	loading: false,
}

const AccountContext: Context<{
	account: IAccount
	actions: TAccountActions
}> = createContext({
	account: initialAccountState,
	actions: {
		setError: () => {},
		getAccountDetails: () => {},
		getChannels: () => {},
		updateAccount: () => {},
		updateAdditionalInfo: () => {},
		getCallEmail: () => {},
	},
})

export const AccountProvider = (props: PropsWithChildren<any>) => {
	const [loading, setLoading] = useState<boolean>(true)
	const [error, setProviderError] = useState<string | null>(null)
	const [user, setUser] = useState<IUser>()
	const [callEmail, setCallEmail] = useState<string>('')
	const [channels, setChannels] = useState<TChannel[]>(initialAccountState.channels)
	const [token, setToken] = useState<string>()
	const { accountId } = useParams() as UrlParam

	useEffect(() => {
		setUser((prevUser) => ({
			...prevUser,
			callEmail,
		}))
	}, [callEmail])

	useEffect(() => {
		if (accountId) {
			getAccountDetails(accountId)
			getChannels(accountId)
		} else {
			setLoading(false)
		}
	}, [accountId])

	useEffect(() => {
		if (token && !callEmail) {
			getCallEmail()
		}
	}, [token, callEmail])

	const getChannels = async (accountId: string) => {
		setLoading(true)
		setProviderError(null)
		const channelsResponse: TApiResponse = await callApi('/account/channel/list', {
			method: 'POST',
			body: { userid: accountId },
		})

		const { data } = channelsResponse.payload
		if (channelsResponse.ok) {
			setChannels(data.channels)
		} else {
			setProviderError(data.message || 'Cannot get channel list!')
		}
		setLoading(false)
	}

	const getAccountDetails = async (accountId: string) => {
		setLoading(true)
		setProviderError(null)
		const accountResponse: TApiResponse = await callApi('/account/profile', {
			method: 'POST',
			body: { userid: accountId },
		})

		const { data } = accountResponse.payload
		if (accountResponse.ok) {
			const newUser = {
				...data.user,
				phone_country_id: String(data.user.phone_country_id),
			}
			setToken(data.token)
			setUser(newUser)
		} else {
			setProviderError(data.message || 'Cannot login!')
		}
		setLoading(false)
	}

	const setError = useCallback(
		(errorMessage?: string) => {
			setProviderError(errorMessage || null)
		},
		[setProviderError]
	)

	const fetchUpdateAccount = async (body: TAccountRequest) => {
		setProviderError(null)
		setLoading(true)
		const registerResponse: TApiResponse = await callApi('account', {
			method: 'PUT',
			body,
			headers: profileHeaderToken(token),
		})
		setLoading(false)

		const { data } = registerResponse.payload

		if (registerResponse.ok) {
			const newUser = {
				...user,
				...data,
				phone_country_id: data.phone_country_id ? String(data.phone_country_id) : String(user.phone_country_id) || '1',
			}
			setUser(newUser)
		} else {
			setProviderError(data.message || 'Cannot Update Account!')
		}
	}

	const fetchUpdateAdditionalInfo = async (body: TRequestWithCustom<TEeoRequest>) => {
		setProviderError(null)
		setLoading(true)
		const additionalInfoResponse: TApiResponse = await callApi('account/additional-info', {
			method: 'PUT',
			body,
			headers: profileHeaderToken(token),
		})
		setLoading(false)

		const { data } = additionalInfoResponse.payload

		if (additionalInfoResponse.ok) {
			const newUser = {
				...user,
				...data.user,
			}
			setUser(newUser)
		} else {
			setProviderError(data.message || 'Cannot Update Account!')
		}
	}

	const fetchGetCallEmail = async () => {
		const getCallEmailResponse: TApiResponse = await callApi('account/call-email', {
			method: 'GET',
			headers: profileHeaderToken(token),
		})

		const { data } = getCallEmailResponse.payload

		if (getCallEmailResponse.ok) {
			setCallEmail(getCallEmailText(data.email))
		} else {
			setCallEmail('')
			setProviderError(data.message || 'Cannot get call email!')
		}
	}

	const updateUserDataDeps = [setLoading, setProviderError, setUser, user]
	const updateAccount = useCallback(fetchUpdateAccount, updateUserDataDeps)
	const updateAdditionalInfo = useCallback(fetchUpdateAdditionalInfo, updateUserDataDeps)
	const getCallEmail = useCallback(fetchGetCallEmail, [callEmail, setCallEmail, updateUserDataDeps])

	const actions: TAccountActions = {
		setError,
		getAccountDetails,
		updateAccount,
		updateAdditionalInfo,
		getChannels,
		getCallEmail,
	}

	return (
		<AccountContext.Provider value={{ account: { user, token, loading, error, channels }, actions }}>
			{props.children}
		</AccountContext.Provider>
	)
}

export const useAccountContext: () => {
	account: IAccount
	actions: TAccountActions
} = () => {
	return useContext(AccountContext)
}
